import { BrowserDetection, jitsiLocalStorage } from '@jitsi/js-utils';
import i18next from 'i18next';
import { sha256 } from 'js-sha256';
import jwtDecode from 'jwt-decode';
import _, { isNil } from 'lodash';
import moment from 'moment';
import type { Dispatch } from 'redux';

import {
    CHROME,
    FIREFOX,
    INTERNET_EXPLORER,
    OPERA,
    SAFARI
} from '../../../../browser';
import { updateDropboxToken } from '../../dropbox';
import { getDropboxData } from '../../dropbox/functions.any';
import { showErrorNotification } from '../../notifications';
import { SET_AUTO_LIVE_STREAM_KEYS } from '../../recording/actionTypes';
import {
    RECORDING_TYPES,
    isCustomS3RecordingStorage,
    isLocalRecordingStorage
} from '../../recording/constants';
import { getSessionStatusToShow } from '../../recording/functions';
import { toggleRequestingSubtitles } from '../../subtitles/actions';
import logger from '../app/logger';
import { getCurrentConference } from '../conference/functions';
import { apiKEY } from '../config/constants';
import { JWT_STORAGE, SET_MEETING_INFO, SET_RENDER_CONTENT, setJWT, setJwtStorage } from '../jwt';
import { MHRecordingConstants } from '../lib-meet-hour';
import { getLocalParticipant, getParticipantDisplayName } from '../participants/functions';
import { getBackendSafeRoomName } from '../util';
import { isDomainWeborNative } from '../util/checkOS';

import {
    AUTO_RECORD_STOP, FAKE_PARTICPANT_JOINED, FAKE_PARTICPANT_LEFT, INVALID_MEETING_JSON, IS_RECORDING_AND_LIVESTREAMING_ON, PRE_REG_API,
    SEND_AUTO_RECORD_COMMAND, SET_ACCESS_TOKEN_NATIVE, SET_ATTENDANCE_API, SET_AUTO_PASSWORD, SET_DESKTOP_APP, SET_IS_SWITCHING_BREAKOUT_ROOM, SET_IS_SWITCHING_TO_MAIN_ROOM, SET_PASSWORD_FORM_NATIVE,
    SET_USER_DETAILS, SET_VOICE_COMMAND_STRING
} from './actionTypes';
import { FakeParticipant, ULR_PATHS } from './constants';
import { fetchAxiosApi, getVirtualScreenshareParticipantOwnerId } from './functions';

declare var config: Object;

/**
* Maps the names of the browsers from bowser to the internal names defined in
* ./browsers.js.
*/
const bowserNameToJitsiName = {
    'Chrome': CHROME,
    'Chromium': CHROME,
    'Opera': OPERA,
    'Firefox': FIREFOX,
    'Internet Explorer': INTERNET_EXPLORER,
    'Safari': SAFARI
};

/**
 * Extracts OneDrive token payload from recordsettings response, supporting
 * both nested and flat response shapes.
 *
 * @param {Object} settings - Recordsettings payload.
 * @returns {Object}
 */
function getOneDriveTokenPayload(settings: Object = {}) {
    return settings?.onedrive_access_token || settings;
}

export const recordingandLivestreamingOn = value => {
    return {
        type: IS_RECORDING_AND_LIVESTREAMING_ON,
        isRecordingandLivestreamingOn: value
    };
};

/**
 *  Fecth Api of the conference.
 *
 * @returns {Object}
 */
export function fetchpreRegApi() {
    return async (dispatch: Dispatch<any>, getState: Function) => {
        const room = getBackendSafeRoomName(getState()['features/base/conference'].room);
        const { MH_CLIENT_ID } = getState()['features/base/config'];

        const moement = moment().seconds(0)
            .milliseconds(0)
            .format('X');

        const mesh256 = sha256(`${apiKEY}:MH:${moement}`);
        const pCode = getState()['features/base/mtApi'].md5password;
        const isPCode = isNil(pCode) ? {} : {
            pcode: pCode
        };
        const preRegObject = {
            client_id: MH_CLIENT_ID,
            credentials: mesh256,
            ...isDomainWeborNative,
            'meeting_id': room,
            ...isPCode
        };
        const preRegString = JSON.stringify(preRegObject);
        const state1 = getState()['features/base/jwt'];
        const usersAccessToken = jitsiLocalStorage.getItem('accessToken');
        const isAccessToken = state1.accessToken ?? usersAccessToken === 'null' ? null : usersAccessToken;
        const preRegApi = await fetchAxiosApi('meeting/pre_registration_check',
            {
                method: 'post',
                headers: isAccessToken ? {
                    'Authorization': `Bearer ${isAccessToken}`,
                    'Content-Type': 'application/json'
                } : {
                    'Content-Type': 'application/json'
                },
                body: preRegString
            });
        const preRegData = await preRegApi;

        const isNotValid = Boolean(!preRegData.success);
        const mhAd = document.querySelector('#mhAd') || null;

        const isjwtStored = getState()['features/base/jwt'].jwt ?? getState()['features/base/jwt'].jwtData;

        if (typeof preRegData === 'object' && preRegData.hasOwnProperty('display_ads') && preRegData?.display_ads === false) {
            mhAd && mhAd.remove();
        }

        if (isNotValid && isjwtStored) {
            const jwt = undefined;
            const jwtStorage = undefined;

            dispatch(
                setJWT(jwt));
            dispatch(
                {
                    type: JWT_STORAGE,
                    jwtData: jwtStorage
                });
        }
        const userAccessToken = jitsiLocalStorage.getItem('accessToken') ?? null;
        const accessTokenExists = getState()['features/base/jwt'].accessToken ?? userAccessToken === 'null' ? null : userAccessToken;

        if ((_.isNil(isjwtStored) && _.isNil(accessTokenExists)) || config?.iAmRecorder === true) {
            dispatch(setJWTforGuest());
            dispatch(setMeetingDetailsinJWT());

        }

        return dispatch({
            type: PRE_REG_API,
            preRegData

        });

    };
}

/**
 *  Fecth Api of the conference.
 *
 * @returns {Object}
 */
export function fetchPreRegNativeApi() {
    return async (dispatch: Dispatch<any>, getState: Function) => {
        try {
            // First, dispatch an action to indicate loading
            dispatch({
                type: PRE_REG_API,
                preRegData: { loading: true }
            });

            const room = getBackendSafeRoomName(getState()['features/base/conference'].room);
            const { API_URL_PREFIX, API_VERSION_PREFIX, MH_CLIENT_ID } = getState()['features/base/config'];
            const moement = moment().seconds(0)
                .milliseconds(0)
                .format('X');

            const mesh256 = sha256(`${apiKEY}:MH:${moement}`);
            const pCode = getState()['features/base/mtApi'].md5password;
            const isPCode = isNil(pCode) ? {} : {
                pcode: pCode
            };
            const preRegObject = {
                client_id: MH_CLIENT_ID,
                credentials: mesh256,
                ...isDomainWeborNative,
                'meeting_id': room,
                ...isPCode
            };

            const preRegString = JSON.stringify(preRegObject);
            const preRegData = await fetchAxiosApi('meeting/pre_registration_check', {
                method: 'post',
                body: preRegString,
                headers: {
                    'Content-Type': 'application/json'
                },
                config: {
                    API_URL_PREFIX,
                    API_VERSION_PREFIX
                }
            });

            const isNotValid = Boolean(!preRegData.success);
            const isjwtStored = getState()['features/base/jwt'].jwt ?? getState()['features/base/jwt'].jwtData;

            if (isNotValid && isjwtStored) {
                const jwt = undefined;
                const jwtStorage = undefined;

                dispatch(setJWT(jwt));
                dispatch({
                    type: JWT_STORAGE,
                    jwtData: jwtStorage
                });
            }

            const accessTokenExists = getState()['features/base/jwt']?.accessToken;

            if (_.isNil(isjwtStored) && _.isNil(accessTokenExists)) {
                dispatch(setJWTforGuest());
            }

            return dispatch({
                type: PRE_REG_API,
                preRegData
            });
        } catch (error) {
            logger.error('Error in fetchPreRegNativeApi:', error);

            // Dispatch error state
            return dispatch({
                type: PRE_REG_API,
                preRegData: { success: false,
                    error: error.message }
            });
        }
    };
}

/**
 * Sets the default page index of {@link WelcomePageLists}.
 *
 * @param {number} userd - Object details of the user.
 * @returns {{
 *     type: SET_WELCOME_PAGE_LISTS_DEFAULT_PAGE,
 *     pageIndex: number
 * }}
 */
export function setuserDetails(userd: ?Object) {
    return {
        type: SET_USER_DETAILS,
        userd
    };
}

export const setAccessTokenNative = (tokn: string) => {
    return {
        type: SET_ACCESS_TOKEN_NATIVE,
        tokn
    };
};

export const setPasswordForm = (formBool: boolean) => {

    return {
        type: SET_PASSWORD_FORM_NATIVE,
        formBool
    };
};

export const setAttendanceApi = logout => {
    return {
        type: SET_ATTENDANCE_API,
        isLogout: logout ? logout : false
    };
};

export const setInvalidMeeting = data => {
    return {
        type: INVALID_MEETING_JSON,
        data
    };
};

/**
 *  User Attendance when it joins the conference.
 *
 * @param {Object} getState - State Reducer .
 * @returns {any}
 */
export async function sendUserAttendance({ getState, isLogout = false }) {
    const state = getState();
    const { API_URL_PREFIX, API_VERSION_PREFIX, MH_CLIENT_ID } = state['features/base/config'];

    try {
        const format1 = 'YYYY-MM-DD HH:mm:ss';
        const currentDateTime = moment().utc()
            .format(format1);
        const bowser = new BrowserDetection();
        const browserName = bowser.getName();
        let accessType = 'web';
        const participant = getLocalParticipant(state);

        for (const i in bowserNameToJitsiName) {
            if (i !== browserName) {
                accessType = 'app';
            }
        }
        const roomName = getState()['features/base/conference'].room;

        if (!roomName) {
            logger.warn('Skipping the user attendance record: no conference to attribute it to '
                + `(isLogout=${String(isLogout)}, hasLocalParticipant=${Boolean(participant)})`);

            return;
        }

        let dataJson;

        try {
            const ClientIPData = await fetchAxiosApi('https://api.ipify.org/?format=json', {
                timeout: 20000,
                method: 'get',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (typeof ClientIPData !== 'undefined') {

                const geoData = await fetchAxiosApi(`${API_URL_PREFIX}${API_VERSION_PREFIX}geolocation?api_key=${apiKEY}&ip_address=${ClientIPData?.ip}`, {
                    timeout: 20000,
                    method: 'get',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                if (geoData) {
                    dataJson = geoData;
                }
            }
        } catch (error) {
            logger.error('Error in fetching geolocation bros', error);

        }

        const { occurenceId } = state['features/base/mtApi'];

        const moement = moment().seconds(0)
            .milliseconds(0)
            .format('X');

        const mesh256 = sha256(`${apiKEY}:MH:${moement}`);

        let userObject = {};

        if (isLogout === true) {
            userObject = {
                logout_datetime: currentDateTime

            };
        } else {
            userObject = {
                login_datetime: currentDateTime

            };
        }

        userObject = {
            ...userObject,
            client_id: MH_CLIENT_ID,
            credentials: mesh256,
            ...isDomainWeborNative,
            meeting_id: roomName,
            name: participant?.name,
            occurrence_id: occurenceId,
            is_host: state['features/base/jwt'].jwt ? 1 : 0,
            email_id: participant?.email,
            user_agent: browserName,
            access_type: accessType,
            ip: dataJson?.IPv4,
            geolocation: dataJson
        };

        const userJson = JSON.stringify(userObject);

        await fetchAxiosApi('meeting/save_meeting_attendance', {
            method: 'post',
            timeout: 20000,
            headers: { 'Content-Type': 'application/json' },
            body: userJson,
            config: {
                API_URL_PREFIX,
                API_VERSION_PREFIX
            }
        });

        // await sendUserData.json();
    } catch (e) {
        // This is the outer handler for the whole attendance submission, not for geolocation - that has
        // its own catch above. The old wording sent every failure here to the logs under a cause it did
        // not have, with the error itself discarded.
        logger.error('Error in sending user attendance', e);
    }
}

export const setVoiceCommandString = (strng: string) => {
    return {
        type: SET_VOICE_COMMAND_STRING,
        vsString: strng
    };
};

/**
 * Sets jwt to guests user for interface config and config overwrite.
 *
 * @returns {any}
 */
export function setJWTforGuest() {
    return async (dispatch: Dispatch<any>, getState: Function) => {
        const room = getBackendSafeRoomName(getState()['features/base/conference'].room);
        const isjwtStored = getState()['features/base/jwt'].jwt ?? getState()['features/base/jwt'].jwtData;
        const conf = getState()['features/base/config'];
        const moement = moment().seconds(0)
            .milliseconds(0)
            .format('X');

        const mesh256 = sha256(`${apiKEY}:MH:${moement}`);

        if (_.isNil(isjwtStored)) {
            const referenceParam = {
                'meeting_id': room,
                client_id: conf.MH_CLIENT_ID,
                credentials: mesh256,
                ...isDomainWeborNative
            };
            const jwtResponseForGuest = await fetchAxiosApi('getjwt',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': '*'
                    },
                    body: referenceParam,
                    config: conf
                }).catch(err => logger.error('error in api', err));

            if (jwtResponseForGuest?.success && jwtResponseForGuest.jwt) {
                const jwt = jwtDecode(jwtResponseForGuest.jwt);

                dispatch({
                    type: SET_MEETING_INFO,
                    meeting: jwt.meeting
                });
            }

            if ((jwtResponseForGuest?.success && conf.interfaceConfig?.applyMeetingSettings === true)
                    || (jwtResponseForGuest.jwt && conf?.iAmRecorder === true)) {
                dispatch(setJwtStorage(jwtResponseForGuest.jwt));
            }

            return dispatch({
                type: SET_RENDER_CONTENT,
                shouldRender: true
            });

        }
    };
}

/**
 * Sets jwt to guests user for interface config and config overwrite.
 *
 * @returns {any}
 */
export function setMeetingDetailsinJWT() {
    return async (dispatch: Dispatch<any>, getState: Function) => {
        const room = getBackendSafeRoomName(getState()['features/base/conference'].room);
        const isjwtStored = getState()['features/base/jwt'].jwt ?? getState()['features/base/jwt'].jwtData;
        const conf = getState()['features/base/config'];
        const userAccessToken = jitsiLocalStorage.getItem('accessToken') ?? null;
        const moement = moment().seconds(0)
            .milliseconds(0)
            .format('X');

        const mesh256 = sha256(`${apiKEY}:MH:${moement}`);

        if (_.isNil(isjwtStored)) {
            const referenceParam = {
                'meeting_id': room,
                client_id: conf.MH_CLIENT_ID,
                credentials: mesh256,
                ...isDomainWeborNative
            };
            const jwtResponseForGuest = await fetchAxiosApi('getjwt',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': '*'
                    },
                    body: referenceParam,
                    config: conf
                }).catch(err => logger.error('error in api', err));
            const accessTokenExists = getState()['features/base/jwt'].accessToken ?? userAccessToken === 'null' ? null : userAccessToken;

            if (jwtResponseForGuest?.success && jwtResponseForGuest.jwt) {
                const jwt = jwtDecode(jwtResponseForGuest.jwt);

                dispatch({
                    type: SET_MEETING_INFO,
                    meeting: jwt.meeting
                });
            }

            if (_.isNil(isjwtStored) && _.isNil(accessTokenExists)) {
                const jwt = jwtDecode(jwtResponseForGuest.jwt);

                dispatch({
                    type: SET_MEETING_INFO,
                    meeting: jwt.meeting
                });
            }

        }
    };
}

/**
 * Auto Live Streaming.
 *
 * @param {any} {dispatch
 * @param {any} getState}
 * @returns {any}
 */
export async function _autoLiveStreaming({ dispatch, getState }) {
    const state = getState();
    const { accessToken, confSettings } = state['features/base/jwt'];

    const { transcription, dropbox = {}, onedrive = {}, ...mbConfig } = state['features/base/config'];
    const { _requestingSubtitles } = state['features/subtitles'];
    const { stopAutoRecord = false } = state['features/base/mtApi'];

    if (Boolean(stopAutoRecord) || (confSettings?.AUTO_START_RECORDING !== 1 && confSettings?.AUTO_START_LIVESTREAMING !== 1)) {
        return;
    }

    const conference = getCurrentConference(state);
    const userId = state['features/base/jwt']?.meetingDetails;
    const room = getBackendSafeRoomName(getState()['features/base/conference'].room);
    const { MH_CLIENT_ID, API_URL_PREFIX, API_VERSION_PREFIX } = getState()['features/base/config'];
    const recordURL = `${API_URL_PREFIX}${API_VERSION_PREFIX}customer/saverecordings`;
    const moement = moment().seconds(0)
        .milliseconds(0)
        .format('X');

    const mesh256 = sha256(`${apiKEY}:MH:${moement}`);

    const liveStreamObject = {
        client_id: MH_CLIENT_ID,
        credentials: mesh256,
        ...isDomainWeborNative,
        'meeting_id': room
    };
    const headers = accessToken ? {
        'Content-Type': 'application/json',
        'Authorization': `${'Bearer '}${accessToken}`
    } : {
        'Content-Type': 'application/json'
    };
    const getLiveStreamData = await fetchAxiosApi(ULR_PATHS.AUTO_LIVE_STREAM, {
        method: 'POST',
        headers,
        body: liveStreamObject,
        config: mbConfig
    });
    const getRecData = await fetchAxiosApi(ULR_PATHS.AUTO_REC_STREAM, {
        method: 'POST',
        headers,
        body: liveStreamObject,
        config: mbConfig
    });

    const recordingStorage = getRecData?.default_recording_storage;
    const isDropboxStorage = recordingStorage === 'Dropbox';
    const isOneDriveStorage = recordingStorage === 'OneDrive';
    const isCustomS3Storage = isCustomS3RecordingStorage(recordingStorage);
    const isMeetHourStorage = isLocalRecordingStorage(recordingStorage);

    const parsedS3FreeSpace = Number.parseFloat(`${getRecData?.s3_free_space ?? ''}`);
    const isS3StorageFull = Number.isFinite(parsedS3FreeSpace) && parsedS3FreeSpace <= 0;
    const isAutoRecordingStorageFull
        = getRecData?.code === 1032 || getRecData?.code === 1033 || isS3StorageFull;

    // Stopping auto recording if api is failing or if Meet Hour storage consumption is more than available space
    if (!getRecData || (getRecData && getRecData?.success === false) || isAutoRecordingStorageFull) {
        if (isAutoRecordingStorageFull) {
            dispatch(showErrorNotification({ descriptionKey: i18next.t('liveStreaming.makeSureYouHaveEnoughStorageAvailableOnYourAccount'),
                titleKey: i18next.t('liveStreaming.failToStartAutoRecording') }));
        } else {
            dispatch(showErrorNotification({ descriptionKey: i18next.t('liveStreaming.pleaseContactSupportForAssistance'),
                titleKey: i18next.t('liveStreaming.failToStartAutoRecording') }));
        }

        return;
    }

    // Stopping auto live streaming if api is failing or if Subscription of user doesn't allow livestreaming
    if (!getLiveStreamData || (getLiveStreamData && getLiveStreamData?.success === false) || getRecData?.code === 1206) {
        dispatch(showErrorNotification({ descriptionKey: i18next.t('liveStreaming.pleaseContactSupportForAssistance'),
            titleKey: i18next.t('liveStreaming.failToStartAutoLiveStreaming') }));

        return;
    }

    if (getRecData?.default_recording_storage === 'Dropbox') {
        dispatch(updateDropboxToken(getRecData?.settings?.dropbox_access_token?.access_token,
            getRecData?.settings?.dropbox_access_token?.refresh_token, getRecData?.settings?.dropbox_access_token?.expires));

    }
    const oneDriveTokenPayload = getOneDriveTokenPayload(getRecData?.settings);
    let oneDriveToken = oneDriveTokenPayload?.access_token;
    let oneDriveRefreshToken = oneDriveTokenPayload?.refresh_token;
    let oneDriveExpireDate = oneDriveTokenPayload?.expires || oneDriveTokenPayload?.expires_in;
    const decryptUrl = `${API_URL_PREFIX}${API_VERSION_PREFIX}customer/recordsettings_decrypt`;

    const streamStatus = getSessionStatusToShow(state, MHRecordingConstants.mode.STREAM);
    const recStatus = getSessionStatusToShow(state, MHRecordingConstants.mode.FILE);

    const shouldUseUploadCredentials
        = isDropboxStorage || isOneDriveStorage || isCustomS3Storage || isMeetHourStorage;
    const recData = isDropboxStorage ? {
        'token': getRecData?.settings?.dropbox_access_token?.access_token
    } : isOneDriveStorage ? {
        'token': oneDriveToken
    } : {
        settings: getRecData.settings,
        storage: getRecData.default_recording_storage,
        s3_free_space: getRecData?.s3_free_space ? getRecData?.s3_free_space : null,
        s3_used_space: getRecData?.s3_used_space ? getRecData?.s3_used_space : null
    };

    const shouldValidateOneDriveAutoRecording = isOneDriveStorage
        && (confSettings?.AUTO_START_RECORDING === 1 || getLiveStreamData?.data?.Recording?.enable === true);
    const shouldValidateDropboxAutoRecording = isDropboxStorage
        && (confSettings?.AUTO_START_RECORDING === 1 || getLiveStreamData?.data?.Recording?.enable === true);

    if (shouldValidateDropboxAutoRecording) {
        const dropboxToken = getRecData?.settings?.dropbox_access_token?.access_token;
        const dropboxData = await getDropboxData(dropboxToken, dropbox?.appKey);

        if (!dropboxData) {
            dispatch(showErrorNotification({
                titleKey: i18next.t('recording.dropboxError'),
                descriptionKey: i18next.t('recording.dropboxProfileLoadFailed')
            }));

            return;
        }

        if (typeof dropboxData?.spaceLeft === 'number' && dropboxData.spaceLeft <= 0) {
            dispatch(showErrorNotification({
                descriptionKey: i18next.t('liveStreaming.makeSureYouHaveEnoughStorageAvailableOnYourAccount'),
                titleKey: i18next.t('liveStreaming.failToStartAutoRecording')
            }));

            return;
        }
    }

    // Pulled in only when the recording storage actually is OneDrive. Both branches below need it, so it is
    // resolved once here rather than inside each.
    let getOneDriveData, updateOneDriveToken;

    if (shouldValidateOneDriveAutoRecording || isOneDriveStorage) {
        ({ getOneDriveData, updateOneDriveToken } = await import('../../onedrive'));
    }

    if (shouldValidateOneDriveAutoRecording) {
        let validatedOneDriveToken = oneDriveToken;
        let validatedOneDriveRefreshToken = oneDriveRefreshToken;
        let validatedOneDriveExpireDate = oneDriveExpireDate;

        const oneDriveData = await getOneDriveData(oneDriveToken, onedrive?.clientId, {
            rToken: oneDriveRefreshToken,
            strategy: onedrive?.authStrategy,
            onRefreshed: refreshed => {
                validatedOneDriveToken = refreshed.token;
                validatedOneDriveRefreshToken = refreshed.rToken;
                validatedOneDriveExpireDate = refreshed.expireDate;
            }
        });

        if (!oneDriveData) {
            dispatch(showErrorNotification({
                titleKey: 'recording.oneDriveError',
                descriptionKey: 'recording.oneDriveProfileLoadFailed'
            }));

            return;
        }

        if (typeof oneDriveData?.spaceLeft === 'number' && oneDriveData.spaceLeft <= 0) {
            dispatch(showErrorNotification({
                descriptionKey: i18next.t('liveStreaming.makeSureYouHaveEnoughStorageAvailableOnYourAccount'),
                titleKey: i18next.t('liveStreaming.failToStartAutoRecording')
            }));

            return;
        }

        oneDriveToken = validatedOneDriveToken;
        oneDriveRefreshToken = validatedOneDriveRefreshToken;
        oneDriveExpireDate = validatedOneDriveExpireDate;

        dispatch(updateOneDriveToken(
            oneDriveToken,
            oneDriveRefreshToken,
            oneDriveExpireDate
        ));

        recData.token = oneDriveToken;
    } else if (isOneDriveStorage) {
        dispatch(updateOneDriveToken(oneDriveToken, oneDriveRefreshToken, oneDriveExpireDate));
    }

    dispatch({
        type: SET_AUTO_LIVE_STREAM_KEYS,
        autoLiveStreamKeys: getLiveStreamData?.data
    });

    if ((typeof recStatus === 'undefined' || recStatus === MHRecordingConstants.status.OFF)
        && confSettings?.AUTO_START_RECORDING === 1 && (!confSettings.AUTO_START_LIVESTREAMING || confSettings.AUTO_START_LIVESTREAMING !== 1)) {
        const appData = shouldUseUploadCredentials
            ? JSON.stringify({
                'file_recording_metadata': {
                    'upload_credentials': {
                        'service_name': isDropboxStorage
                            ? RECORDING_TYPES.DROPBOX
                            : isOneDriveStorage
                                ? RECORDING_TYPES.ONEDRIVE
                                : 'awss3',
                        'recordingType': recordingStorage,
                        ...recData,
                        'userid': userId?.id,
                        _accessToken: accessToken,
                        client_id: MH_CLIENT_ID,
                        onedrive_client_id: onedrive?.clientId,
                        credentials: mesh256,
                        app_key: dropbox.appKey,
                        r_token: isOneDriveStorage
                            ? oneDriveRefreshToken
                            : typeof getRecData?.settings === 'object'
                                ? getRecData?.settings?.dropbox_access_token?.refresh_token
                                : null,
                        ...isDomainWeborNative,
                        decryptUrl,
                        'urlSaved': recordURL,
                        api_key: apiKEY,
                        'startTiming': new Date().getTime(),

                        'meetingid': room
                    }
                }
            })
            : JSON.stringify({
                'file_recording_metadata': {
                    'share': true
                }
            });

        conference.startRecording({
            mode: MHRecordingConstants.mode.FILE,
            appData
        });
        conference.sendCommand(SEND_AUTO_RECORD_COMMAND, {
            value: true
        });

        if (transcription?.enabled && (confSettings?.AUTO_CAPTION_ON_RECORDING ?? transcription?._autoCaptionOnRecord)) {
            if (confSettings?.TRANSCRIPTION_QUOTA_EXCEEDED === 1 ?? false) {
                dispatch(showErrorNotification(
                    {
                        descriptionKey: i18next.t('transcribing.transcriptionQuotaExceeded'),
                        titleKey: i18next.t('transcribing.transcriptionQuotaExceededTitle')
                    }));
            } else if (_requestingSubtitles === true) {
                // no action
            } else {
                dispatch(toggleRequestingSubtitles());
            }
        }
    }

    if ((((streamStatus === MHRecordingConstants.status.OFF || typeof streamStatus === 'undefined')
        && (recStatus === MHRecordingConstants.status.OFF || typeof recStatus === 'undefined'))) && confSettings?.AUTO_START_LIVESTREAMING === 1) {
        const appData = shouldUseUploadCredentials
            ? JSON.stringify({
                'file_recording_metadata': {
                    'upload_credentials': {
                        'service_name': isDropboxStorage
                            ? RECORDING_TYPES.DROPBOX
                            : isOneDriveStorage
                                ? RECORDING_TYPES.ONEDRIVE
                                : 'awss3',
                        'recordingType': recordingStorage,
                        ...recData,
                        'userid': userId?.id,
                        _accessToken: accessToken,
                        client_id: MH_CLIENT_ID,
                        onedrive_client_id: onedrive?.clientId,
                        credentials: mesh256,
                        app_key: dropbox.appKey,
                        r_token: isOneDriveStorage
                            ? oneDriveRefreshToken
                            : typeof getRecData?.settings === 'object'
                                ? getRecData?.settings?.dropbox_access_token?.refresh_token
                                : null,
                        ...isDomainWeborNative,
                        'urlSaved': recordURL,
                        api_key: apiKEY,
                        decryptUrl,
                        'startTiming': new Date().getTime(),

                        'meetingid': room
                    }
                }
            })
            : JSON.stringify({
                'file_recording_metadata': {
                    'share': true
                }
            });

        const liveStreamData = Object.values(getLiveStreamData.data).filter(it => it.key !== undefined)
.map(its => its.key);

        if (getLiveStreamData.data?.Recording?.enable === true && liveStreamData.length) {
            dispatch(recordingandLivestreamingOn(true));
            conference.sendCommand(IS_RECORDING_AND_LIVESTREAMING_ON, {
                value: true
            });
        }

        const liveStreamEndObj = getLiveStreamData.data?.Recording?.enable === true && liveStreamData.length ? {
            mode: MHRecordingConstants.mode.FILE,
            appData,
            streamId: liveStreamData[0] ? liveStreamData[0] : null,
            fbstreamId: liveStreamData[1] ? liveStreamData[1] : null
        } : getLiveStreamData.data?.Recording?.enable === true ? {
            mode: MHRecordingConstants.mode.FILE,
            appData
        } : {
            mode: MHRecordingConstants.mode.STREAM,
            streamId: liveStreamData[0] ? liveStreamData[0] : null,
            fbstreamId: liveStreamData[1] ? liveStreamData[1] : null,
            igStreamid: liveStreamData[2] ? liveStreamData[2] : null
        };

        conference.startRecording(liveStreamEndObj);
        conference.sendCommand(SEND_AUTO_RECORD_COMMAND, {
            value: true
        });

        if (transcription?.enabled && (confSettings?.AUTO_CAPTION_ON_RECORDING ?? transcription?._autoCaptionOnRecord)) {
            if (confSettings?.TRANSCRIPTION_QUOTA_EXCEEDED === 1 ?? false) {
                dispatch(showErrorNotification(
                    {
                        descriptionKey: i18next.t('transcription.transcriptionQuotaExceeded'),
                        titleKey: i18next.t('transcription.transcriptionQuotaExceededTitle')
                    }));
            } else if (_requestingSubtitles === true) {
                // no action
            } else {
                dispatch(toggleRequestingSubtitles());
            }
        }
    }

}

export const setAutoPassword = pssword => {
    return {
        type: SET_AUTO_PASSWORD,
        md5password: pssword
    };
};

export const autoRecordStop = autostop => {
    return {
        type: AUTO_RECORD_STOP,
        stopAutoRecord: autostop
    };
};

export const setDesktopApp = val => {
    return {
        type: SET_DESKTOP_APP,
        desktopapp: val
    };
};

export const setIsSwitchingBreakoutRoom = isSwitchingBreakOutRoom => {
    return {
        type: SET_IS_SWITCHING_BREAKOUT_ROOM,
        isSwitchingBreakOutRoom
    };
};

export const setIsSwitchingToMainRoom = isSwitchingToMainRoom => {
    return {
        type: SET_IS_SWITCHING_TO_MAIN_ROOM,
        isSwitchingToMainRoom
    };
};

/**
 * Action to create a virtual screenshare participant.
 *
 * @param {(string)} sourceName - The source name of the JitsiTrack instance.
 * @param {(boolean)} local - Whether it's a local or remote participant.
 * @param {JitsiConference} conference - The conference instance for which the participant is to be created.
 * @returns {Function}
 */
export function createVirtualScreenshareParticipant(sourceName, local, conference) {
    return (dispatch, getState) => {
        const state = getState();
        const ownerId = getVirtualScreenshareParticipantOwnerId(sourceName);
        const ownerName = getParticipantDisplayName(state, ownerId);

        dispatch(fakeParticipantJoined({
            conference,
            fakeParticipant: local ? FakeParticipant.LocalScreenShare : FakeParticipant.RemoteScreenShare,
            id: sourceName,
            name: ownerName
        }));
    };
}

/**
 * Action to signal that a fake participant has joined.
 *
 * @param {Object} payload - The payload object.
 * @param {JitsiConference} payload.conference - The conference instance.
 * @param {string} payload.fakeParticipant - The type of fake participant.
 * @param {string} payload.id - The ID of the fake participant.
 * @param {string} payload.name - The name of the fake participant.
 * @returns {{
 *     type: FAKE_PARTICPANT_JOINED,
 *     conference: JitsiConference,
 *     fakeParticipant: string,
 *     id: string,
 *     name: string
 * }}
 */
export function fakeParticipantJoined({ conference, fakeParticipant, id, name }) {
    return {
        type: FAKE_PARTICPANT_JOINED,
        conference,
        fakeParticipant,
        id,
        name
    };
}

/**
 * Action to signal that a fake participant has left.
 *
 * @param {string} id - The ID of the fake participant.
 * @param {JitsiConference} conference - The conference instance.
 * @param {Object} options - Additional options.
 * @param {string} options.fakeParticipant - The type of fake participant.
 * @returns {{
 *     type: FAKE_PARTICPANT_LEFT,
 *     id: string,
 *     conference: JitsiConference,
 *     options: Object
 * }}
 */
export function fakeParticipantLeft(id, conference, options) {
    return {
        type: FAKE_PARTICPANT_LEFT,
        id,
        conference,
        options
    };
}
